import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH, API_UPDATE } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

// ========================================
// TYPES
// ========================================
export interface SearchedUser {
  userId: string;
  workdayId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNbr: string;
  siteCd: string;
  skills: string[];
  team?: string;
  status: string;
  role: 'User' | 'Supervisor' | 'Administrator' | 'SuperUser';
  isSuperUser: boolean;   
}

export interface UpdateUserProfilePayload {
  workdayId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNbr?: string;
  status?: string;
  skills?: string[];
  team?: string;
  eventAction?: string[];
}

interface SearchedUserState {
  user: SearchedUser | null;
  loading: boolean;
  error: string | null;
  updating: boolean;
  updateError: string | null;
}

const initialState: SearchedUserState = {
  user: null,
  loading: false,
  error: null,
  updating: false,
  updateError: null,
};

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Map API response to SearchedUser type
 * Centralizes the mapping logic to avoid duplication
 */
const mapResponseToSearchedUser = (response: any, fallbackWorkdayId?: string): SearchedUser => {
  return {
    userId: response.workdayId || response.userId || fallbackWorkdayId || '',
    workdayId: response.workdayId || fallbackWorkdayId || '',
    firstName: response.firstName || '',
    lastName: response.lastName || '',
    email: response.email || '',
    phoneNbr: response.phoneNbr || '',
    siteCd: response.siteCd || '',
    skills: Array.isArray(response.skills) ? response.skills : [],
    team: response.team || response.teamCd || '',
    status: response.status || '',
    role: response.role || 'User',
    isSuperUser: response.isSuperUser || false,
  };
};

// ========================================
// ASYNC THUNKS
// ========================================

/**
 * Fetch a user profile by their employee ID
 * Uses: GET /api/user/search?workdayId={workdayId}
 */
export const fetchSearchedUser = createAsyncThunk(
  'searchedUser/fetchSearchedUser',
  async (workdayId: string, { rejectWithValue }) => {
    try {
      const response = await API_FETCH(
        `${ENDPOINTS.GET_USER_PROFILE}?workdayId=${workdayId}`
      );
      
      return mapResponseToSearchedUser(response);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch user profile');
    }
  }
);

/**
 * Update a searched user's profile (HR/Manager only)
 * Uses: PUT /api/User/profile/{workdayId}
 */
export const updateSearchedUserProfile = createAsyncThunk(
  'searchedUser/updateProfile',
  async (payload: UpdateUserProfilePayload, { rejectWithValue }) => {
    try {
      if (!payload.workdayId) {
        return rejectWithValue('Workday ID is required for profile update');
      }

      const { workdayId, ...updateData } = payload;

      const url = `${ENDPOINTS.UPDATE_USER_PROFILE}/${workdayId}`;
      const response = await API_UPDATE(url, updateData);

      return mapResponseToSearchedUser(response, workdayId);
    } catch (error: any) {
      return rejectWithValue(
        error?.data?.message || error?.message || 'Failed to update profile'
      );
    }
  }
);

// ========================================
// SLICE
// ========================================
const searchedUserSlice = createSlice({
  name: 'searchedUser',
  initialState,
  reducers: {
    clearSearchedUser: (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
      state.updating = false;
      state.updateError = null;
    },
    clearError: (state) => {
      state.error = null;
      state.updateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch searched user
      .addCase(fetchSearchedUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.user = null;
      })
      .addCase(fetchSearchedUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchSearchedUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update searched user profile
      .addCase(updateSearchedUserProfile.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(updateSearchedUserProfile.fulfilled, (state, action) => {
        state.updating = false;
        state.user = action.payload;
      })
      .addCase(updateSearchedUserProfile.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload as string;
      });
  },
});

export const { clearSearchedUser, clearError } = searchedUserSlice.actions;
export default searchedUserSlice.reducer;